import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { User, Shield, Cpu, Rocket, Ghost, Activity, Wallet, Zap, Key, CheckCircle, AlertCircle, Crown, TrendingUp, BarChart3, Camera, X, Gift, Loader2, Edit3, Check, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useApp } from '../contexts/AppContext';
import { GlassCard } from './SharedUI';
import { apiFetch } from '../services/apiClient';
import { toast } from 'sonner';
import { appConfirm } from '@/lib/appConfirm';

export function Profile({ t }: { t: any }) {
  const { user, agents, refreshUser, logout } = useApp();
  const [health, setHealth] = useState<any>(null);
  const [passwords, setPasswords] = useState({ current: '', next: '' });
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null, message: string }>({ type: null, message: '' });
  const [isUpdating, setIsUpdating] = useState(false);

  // Redeem modal
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemResult, setRedeemResult] = useState<{ type: 'success' | 'error' | null, message: string }>({ type: null, message: '' });

  // Avatar & nickname editing
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);
  const [editNickname, setEditNickname] = useState(false);
  const [nicknameValue, setNicknameValue] = useState(user?.displayName || '');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    apiFetch('/api/health', { signal: controller.signal })
      .then(res => res.json())
      .then(data => setHealth(data))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (user?.displayName) setNicknameValue(user.displayName);
  }, [user?.displayName]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    setStatus({ type: null, message: '' });

    try {
      const res = await apiFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: passwords.current, newPassword: passwords.next })
      });

      const data = await res.json();
      if (res.ok) {
        setStatus({ type: 'success', message: t.passwordChanged || '密码修改成功' });
        setPasswords({ current: '', next: '' });
      } else {
        setStatus({ type: 'error', message: data.error || t.passwordError || '密码修改失败' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: t.passwordError || '密码修改失败' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeemLoading(true);
    setRedeemResult({ type: null, message: '' });
    try {
      const res = await apiFetch('/api/auth/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: redeemCode.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setRedeemResult({ type: 'success', message: `兑换成功！+${data.amount} Credits，当前余额 ${data.newBalance}` });
        setRedeemCode('');
        refreshUser();
      } else {
        setRedeemResult({ type: 'error', message: data.error || '兑换失败' });
      }
    } catch {
      setRedeemResult({ type: 'error', message: '网络错误，请重试' });
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('图片不能超过 2MB'); return; }
    setAvatarUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        if (!fileInputRef.current) return; // 组件已卸载
        const base64 = reader.result as string;
        const res = await apiFetch('/api/auth/update-profile', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ photoURL: base64 }),
        });
        if (res.ok) {
          refreshUser();
          toast.success('头像已更新');
        } else {
          const d = await res.json().catch(() => ({}));
          toast.error(d.error || '头像更新失败');
        }
        setAvatarUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setAvatarUploading(false);
      toast.error('头像上传失败');
    }
  };

  const handleSaveNickname = async () => {
    if (!nicknameValue.trim()) return;
    try {
      const res = await apiFetch('/api/auth/update-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: nicknameValue.trim() }),
      });
      if (res.ok) {
        refreshUser();
        setEditNickname(false);
        toast.success('昵称已更新');
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.error || '昵称更新失败');
      }
    } catch {
      toast.error('网络错误');
    }
  };

  const handleLogout = async () => {
    const ok = await appConfirm({ title: t.logout || '退出登录', message: t.logoutConfirm || '确定要退出当前账号吗？', tone: 'danger', confirmText: t.logout || '退出登录' });
    if (!ok) return;
    await logout();
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-12">
      <div className="flex flex-col md:flex-row items-center gap-8">
        <div className="relative w-32 h-32 md:w-48 md:h-48 rounded-full overflow-hidden border-4 border-celestial-saturn/20 shadow-[0_0_50px_rgba(255,204,0,0.1)]" onMouseEnter={() => setShowAvatarEdit(true)} onMouseLeave={() => setShowAvatarEdit(false)}>
          {user.photoURL ? (
            <img src={user.photoURL} alt={user.displayName || user.username} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-full h-full bg-white/[0.07] flex items-center justify-center text-white/40">
              <User size={64} />
            </div>
          )}
          <AnimatePresence>
            {showAvatarEdit && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 flex items-center justify-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center gap-1">
                  {avatarUploading ? <Loader2 size={24} className="animate-spin text-white" /> : <Camera size={24} className="text-white" />}
                  <span className="text-xs text-white/70">{avatarUploading ? '上传中' : '更换头像'}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
        </div>
        
        <div className="text-center md:text-left space-y-4">
          <div className="space-y-1">
            {editNickname ? (
              <div className="flex items-center gap-2 justify-center md:justify-start">
                <Input
                  value={nicknameValue}
                  onChange={(e) => setNicknameValue(e.target.value)}
                  className="text-3xl font-bold bg-white/[0.07] border-white/10 rounded-xl max-w-[280px]"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNickname(); if (e.key === 'Escape') { setEditNickname(false); setNicknameValue(user.displayName || ''); } }}
                />
                <Button size="icon" className="h-10 w-10 rounded-xl bg-green-500/20 text-green-500" onClick={handleSaveNickname}><Check size={18} /></Button>
                <Button size="icon" className="h-10 w-10 rounded-xl bg-white/[0.07] text-white/40" onClick={() => { setEditNickname(false); setNicknameValue(user.displayName || ''); }}><X size={18} /></Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 justify-center md:justify-start">
                <h1 className="text-4xl font-bold tracking-tighter glow-text">{user.displayName || user.username}</h1>
                <button onClick={() => setEditNickname(true)} className="text-white/30 hover:text-white/60 transition-colors"><Edit3 size={16} /></button>
              </div>
            )}
            <p className="text-white/40 font-mono text-sm">{t.nodeId || '节点 ID'}: {user.uid.slice(0, 6)}...{user.uid.slice(-4)}</p>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-3">
            <Badge icon={<Shield size={12} />} label={t.verifiedNode || '已验证节点'} color="text-green-500 bg-green-500/10" />
            <Badge icon={<Cpu size={12} />} label={t.levelArchitect || '12 级架构师'} color="text-celestial-saturn bg-celestial-saturn/10" />
            <Badge icon={<Rocket size={12} />} label={t.earlyExplorer || '早期探索者'} color="text-celestial-mars bg-celestial-mars/10" />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <GlassCard className="p-8 rounded-[3rem] space-y-6" hoverEffect={false}>
          <h3 className="text-xl font-bold tracking-tighter flex items-center gap-2">
            <Wallet size={20} className="text-celestial-saturn" />
            {t.wallet || '神经钱包'}
          </h3>
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-br from-celestial-saturn/20 to-transparent rounded-3xl border border-celestial-saturn/20 text-center space-y-2">
              <div className="text-xs text-white/40 uppercase tracking-widest font-bold">{t.credits || '小枫 Credits'}</div>
              <div className="text-4xl font-bold text-celestial-saturn flex items-center justify-center gap-2">
                <Zap size={32} fill="currentColor" />
                {user.balance || 0}
              </div>
              <div className="text-xs text-white/45">{t.earnCredits || '通过贡献节点算力赚取'}</div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Button className="rounded-xl bg-celestial-saturn text-black font-bold text-xs h-10" onClick={() => { setShowRedeem(true); setRedeemResult({ type: null, message: '' }); setRedeemCode(''); }}>{t.topUp || '充值'}</Button>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-8 rounded-[3rem] space-y-6" hoverEffect={false}>
          <h3 className="text-xl font-bold tracking-tighter">{t.nodeStats || '节点统计'}</h3>
          <div className="grid grid-cols-2 gap-4">
            <StatBox label={t.totalData || '总数据'} value="暂未上线" />
            <StatBox label={t.uptime || '运行时间'} value={health?.runtime?.uptimeSeconds ? `${Math.floor(health.runtime.uptimeSeconds / 3600)}h ${Math.floor((health.runtime.uptimeSeconds % 3600) / 60)}m` : "暂未上线"} />
            <StatBox label={t.nodePower || '节点算力'} value="暂未上线" />
            <StatBox label={t.interactions || '交互次数'} value={health?.database?.interactions?.toLocaleString() || '—'} />
          </div>
        </GlassCard>

        <GlassCard className="p-8 rounded-[3rem] space-y-6" hoverEffect={false}>
          <h3 className="text-xl font-bold tracking-tighter flex items-center gap-2">
            <Crown size={20} className="text-amber-400" />
            {t.subscription || '订阅'}
          </h3>
          <SubscriptionSection t={t} />
        </GlassCard>

        <GlassCard className="p-8 rounded-[3rem] space-y-6" hoverEffect={false}>
          <h3 className="text-xl font-bold tracking-tighter flex items-center gap-2">
            <Key size={20} className="text-celestial-mars" />
            {t.changePassword || '修改密码'}
          </h3>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest font-bold text-white/40">{t.currentPassword || '当前密码'}</label>
              <Input 
                type="password" 
                value={passwords.current}
                onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                className="bg-white/[0.07] border-white/10 rounded-xl"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-widest font-bold text-white/40">{t.newPassword || '新密码'}</label>
              <Input
                type="password"
                value={passwords.next}
                onChange={(e) => setPasswords(prev => ({ ...prev, next: e.target.value }))}
                className="bg-white/[0.07] border-white/10 rounded-xl"
                required
                minLength={6}
                placeholder={t.passwordMinLength || '至少 6 位'}
              />
              {passwords.next && passwords.next.length < 6 && (
                <p className="text-xs text-red-400/70">{t.passwordTooShort || '密码至少需要 6 位字符'}</p>
              )}
            </div>
            
            {status.type && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                  status.type === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                }`}
              >
                {status.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                {status.message}
              </motion.div>
            )}

            <Button 
              type="submit" 
              disabled={isUpdating}
              className="w-full rounded-xl bg-white/[0.07] border border-white/10 hover:bg-white/10 font-bold"
            >
              {isUpdating ? (t.updating || '...') : (t.updatePassword || '更新密码')}
            </Button>
          </form>
        </GlassCard>

        {health && (
          <GlassCard className="p-8 rounded-[3rem] space-y-6 md:col-span-2" hoverEffect={false}>
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold tracking-tighter flex items-center gap-2">
                <Activity size={20} className="text-green-500" />
                {t.healthStatus || '系统健康'}
              </h3>
              <div className="text-xs font-mono text-white/40">{health.timestamp}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-white/[0.07] rounded-2xl border border-white/[0.08]">
                <div className="text-xs text-white/40 uppercase font-bold mb-1">{t.status || '状态'}</div>
                <div className="text-green-500 font-bold uppercase tracking-widest text-xs">{t.operational || '运行中'}</div>
              </div>
              <div className="p-4 bg-white/[0.07] rounded-2xl border border-white/[0.08]">
                <div className="text-xs text-white/40 uppercase font-bold mb-1">{t.users || '用户'}</div>
                <div className="text-white font-bold text-xs">{health.database?.users}</div>
              </div>
              <div className="p-4 bg-white/[0.07] rounded-2xl border border-white/[0.08]">
                <div className="text-xs text-white/40 uppercase font-bold mb-1">{t.agents || '智能体'}</div>
                <div className="text-white font-bold text-xs">{health.database?.agents}</div>
              </div>
              <div className="p-4 bg-white/[0.07] rounded-2xl border border-white/[0.08]">
                <div className="text-xs text-white/40 uppercase font-bold mb-1">{t.latency || '延迟'}</div>
                <div className="text-white/40 font-bold text-xs">—</div>
              </div>
            </div>
          </GlassCard>
        )}
      </div>

      {/* 退出登录 */}
      <div className="flex justify-center pt-2">
        <Button onClick={handleLogout} className="rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 hover:text-red-300 font-bold px-8 h-12 flex items-center gap-2 transition-colors">
          <LogOut size={18} />
          {t.logout || '退出登录'}
        </Button>
      </div>

      {/* Redeem Modal */}
      <AnimatePresence>
        {showRedeem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowRedeem(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0a0a14] border border-white/10 rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Gift size={20} className="text-celestial-saturn" />
                  {t.topUp || '兑换充值'}
                </h3>
                <button onClick={() => setShowRedeem(false)} className="text-white/40 hover:text-white/80 transition-colors"><X size={20} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-white/40 block mb-2">{t.redeemCode || '兑换码'}</label>
                  <Input
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                    placeholder="XF-XXXX-XXXX"
                    className="bg-white/[0.07] border-white/10 rounded-xl text-lg tracking-widest text-center font-mono"
                    maxLength={21}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleRedeem(); }}
                  />
                </div>
                {redeemResult.type && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                      redeemResult.type === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                    }`}
                  >
                    {redeemResult.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                    {redeemResult.message}
                  </motion.div>
                )}
                <Button
                  onClick={handleRedeem}
                  disabled={redeemLoading || !redeemCode.trim()}
                  className="w-full rounded-xl bg-celestial-saturn text-black font-bold h-12 disabled:opacity-50"
                >
                  {redeemLoading ? <Loader2 size={18} className="animate-spin mx-auto" /> : (t.redeem || '兑换')}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SubscriptionSection({ t }: { t: any }) {
  const [sub, setSub] = useState<any>(null);
  const [tokens, setTokens] = useState<any>(null);

  useEffect(() => {
    const controller = new AbortController();
    apiFetch('/api/subscription/status', { signal: controller.signal })
      .then(r => r.json()).then(d => setSub(d?.subscription)).catch(() => {});
    apiFetch('/api/llm/usage?days=1', { signal: controller.signal })
      .then(r => r.json()).then(d => setTokens(d)).catch(() => {});
    return () => controller.abort();
  }, []);

  const cap = sub?.monthlyTokenCap ?? 500000;
  const used = sub?.tokensUsedThisMonth ?? 0;
  const pct = cap > 0 ? Math.round((used / cap) * 100) : 0;
  const todayTotal = tokens?.grandTotal ?? 0;

  const planLabel = sub?.planId === 'pro' ? 'Pro' : sub?.planId === 'enterprise' ? 'Enterprise' : 'Free';
  const planColor = sub?.planId === 'pro' ? 'text-blue-400' : sub?.planId === 'enterprise' ? 'text-purple-400' : 'text-white/40';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-white/[0.07] rounded-2xl border border-white/[0.08]">
        <div>
          <span className="text-xs font-black uppercase tracking-widest text-white/40">{t.currentPlan || '当前套餐'}</span>
          <div className={`text-lg font-bold ${planColor}`}>{planLabel}</div>
        </div>
        <Crown size={28} className="text-amber-400/40" />
      </div>
      <div className="space-y-2">
        <div className="text-xs font-black uppercase tracking-widest text-white/55">{t.monthlyUsage || '月度令牌用量'}</div>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-3 bg-white/[0.07] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(pct, 100)}%` }}
              className={`h-full rounded-full ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-gradient-to-r from-cyan-400 to-blue-500'}`}
            />
          </div>
          <span className="text-sm font-mono font-bold text-white/60">{pct}%</span>
        </div>
        <div className="flex justify-between text-xs text-white/40">
          <span>{used.toLocaleString()} / {cap.toLocaleString()} tokens</span>
          <span>{t.today || '今日'}: {todayTotal.toLocaleString()}</span>
        </div>
      </div>
      {sub?.planId === 'free' && (
        <div className="p-4 bg-amber-500/5 border border-amber-500/10 rounded-2xl text-xs text-amber-300/60 space-y-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} />
            {t.upgradeHint || '升级以解锁更高令牌上限和高级功能。'}
          </div>
          <Button className="w-full rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30 font-bold text-xs h-9" onClick={() => toast.info('升级功能开发中，敬请期待')}>
            {t.upgradePlan || '升级套餐'}
          </Button>
        </div>
      )}
    </div>
  );
}

function Badge({ icon, label, color }: { icon: React.ReactNode; label: string; color: string }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${color}`}>
      {icon}
      {label}
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-4 bg-white/[0.07] rounded-2xl border border-white/[0.08] text-center space-y-1">
      <div className="text-xs text-white/40 uppercase tracking-widest font-bold">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}
